import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Shield, ShieldCheck, ShieldOff, Smartphone, Key, Copy, Check, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';

const Security = () => {
  const { user } = useAuth();
  const [twoFAStatus, setTwoFAStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchTwoFAStatus();
  }, []);

  const fetchTwoFAStatus = async () => {
    try {
      const response = await api.get('/2fa/status');
      setTwoFAStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch 2FA status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetup2FA = async () => {
    setActionLoading(true);
    try {
      const response = await api.post('/2fa/setup');
      setSetupData(response.data);
      setShowSetupDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to setup 2FA');
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerifySetup = async () => {
    if (!verifyCode || verifyCode.length < 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }
    
    setActionLoading(true);
    try {
      await api.post('/2fa/verify-setup', { code: verifyCode });
      toast.success('2FA enabled successfully!');
      setShowSetupDialog(false);
      setShowBackupCodes(true);
      setVerifyCode('');
      fetchTwoFAStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!disableCode || disableCode.length < 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }
    
    setActionLoading(true);
    try {
      await api.post('/2fa/disable', { code: disableCode });
      toast.success('2FA disabled successfully');
      setShowDisableDialog(false);
      setDisableCode('');
      fetchTwoFAStatus();
      setSetupData(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setActionLoading(false);
    }
  };

  const copyBackupCodes = () => {
    if (setupData?.backup_codes) {
      navigator.clipboard.writeText(setupData.backup_codes.join('\n'));
      setCopiedCodes(true);
      toast.success('Backup codes copied to clipboard');
      setTimeout(() => setCopiedCodes(false), 3000);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Security Settings</h1>
          <p className="text-gray-500 mt-1">Manage your account security and two-factor authentication</p>
        </div>

        {/* 2FA Status Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${twoFAStatus?.enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                  {twoFAStatus?.enabled ? (
                    <ShieldCheck className="w-6 h-6 text-green-600" />
                  ) : (
                    <Shield className="w-6 h-6 text-gray-500" />
                  )}
                </div>
                <div>
                  <CardTitle>Two-Factor Authentication (2FA)</CardTitle>
                  <CardDescription>Add an extra layer of security to your account</CardDescription>
                </div>
              </div>
              <Badge variant={twoFAStatus?.enabled ? "default" : "secondary"} className={twoFAStatus?.enabled ? "bg-green-100 text-green-700" : ""}>
                {twoFAStatus?.enabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {twoFAStatus?.enabled ? (
              <>
                <Alert className="bg-green-50 border-green-200">
                  <ShieldCheck className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-700">
                    Your account is protected with two-factor authentication.
                    {twoFAStatus.backup_codes_remaining > 0 && (
                      <span className="block mt-1">
                        You have <strong>{twoFAStatus.backup_codes_remaining}</strong> backup codes remaining.
                      </span>
                    )}
                  </AlertDescription>
                </Alert>
                <Button 
                  variant="outline" 
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => setShowDisableDialog(true)}
                >
                  <ShieldOff className="w-4 h-4 mr-2" />
                  Disable 2FA
                </Button>
              </>
            ) : (
              <>
                <Alert className="bg-amber-50 border-amber-200">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-700">
                    Two-factor authentication is not enabled. We strongly recommend enabling it for accounts with financial access.
                  </AlertDescription>
                </Alert>
                <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                  <Smartphone className="w-8 h-8 text-blue-600 mt-1" />
                  <div>
                    <h4 className="font-medium text-gray-900">How it works</h4>
                    <p className="text-sm text-gray-500 mt-1">
                      After enabling 2FA, you'll need to enter a code from your authenticator app (like Google Authenticator or Authy) 
                      each time you sign in, in addition to your password.
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={handleSetup2FA}
                  disabled={actionLoading}
                  className="bg-blue-900 hover:bg-blue-800"
                >
                  {actionLoading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Setting up...</>
                  ) : (
                    <><Shield className="w-4 h-4 mr-2" /> Enable 2FA</>
                  )}
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Security Tips */}
        <Card>
          <CardHeader>
            <CardTitle>Security Recommendations</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${twoFAStatus?.enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                  {twoFAStatus?.enabled ? (
                    <Check className="w-3 h-3 text-green-600" />
                  ) : (
                    <span className="w-2 h-2 rounded-full bg-gray-400" />
                  )}
                </div>
                <div>
                  <p className="font-medium text-gray-900">Enable two-factor authentication</p>
                  <p className="text-sm text-gray-500">Protect your account with an additional verification step</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center mt-0.5 bg-green-100">
                  <Check className="w-3 h-3 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Use a strong, unique password</p>
                  <p className="text-sm text-gray-500">Never reuse passwords across different services</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center mt-0.5 bg-gray-100">
                  <span className="w-2 h-2 rounded-full bg-gray-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Review active sessions regularly</p>
                  <p className="text-sm text-gray-500">Log out from devices you no longer use</p>
                </div>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Setup 2FA Dialog */}
      <Dialog open={showSetupDialog} onOpenChange={setShowSetupDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Scan the QR code with your authenticator app, then enter the verification code.
            </DialogDescription>
          </DialogHeader>
          
          {setupData && (
            <div className="space-y-4">
              {/* QR Code */}
              <div className="flex justify-center p-4 bg-white rounded-lg border">
                <img 
                  src={setupData.qr_code} 
                  alt="2FA QR Code" 
                  className="w-48 h-48"
                />
              </div>
              
              {/* Manual entry */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Can't scan? Enter this code manually:</p>
                <code className="text-sm font-mono text-gray-900 break-all">{setupData.secret}</code>
              </div>
              
              {/* Verification input */}
              <div className="space-y-2">
                <Label htmlFor="verify-code">Verification Code</Label>
                <Input
                  id="verify-code"
                  type="text"
                  inputMode="numeric"
                  placeholder="Enter 6-digit code"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  className="text-center text-lg tracking-widest"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSetupDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleVerifySetup}
              disabled={actionLoading || verifyCode.length < 6}
              className="bg-blue-900 hover:bg-blue-800"
            >
              {actionLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying...</>
              ) : (
                'Verify & Enable'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backup Codes Dialog */}
      <Dialog open={showBackupCodes} onOpenChange={setShowBackupCodes}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-blue-600" />
              Save Your Backup Codes
            </DialogTitle>
            <DialogDescription>
              Store these codes in a safe place. You can use them to access your account if you lose your authenticator device.
            </DialogDescription>
          </DialogHeader>
          
          {setupData?.backup_codes && (
            <div className="space-y-4">
              <Alert className="bg-amber-50 border-amber-200">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-700">
                  Each code can only be used once. Keep them secret and secure!
                </AlertDescription>
              </Alert>
              
              <div className="grid grid-cols-2 gap-2 p-4 bg-gray-50 rounded-lg font-mono text-sm">
                {setupData.backup_codes.map((code, index) => (
                  <div key={index} className="px-2 py-1 bg-white rounded border text-center">
                    {code}
                  </div>
                ))}
              </div>
              
              <Button 
                variant="outline" 
                className="w-full"
                onClick={copyBackupCodes}
              >
                {copiedCodes ? (
                  <><Check className="w-4 h-4 mr-2" /> Copied!</>
                ) : (
                  <><Copy className="w-4 h-4 mr-2" /> Copy All Codes</>
                )}
              </Button>
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setShowBackupCodes(false)} className="bg-blue-900 hover:bg-blue-800">
              I've Saved My Codes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable 2FA Dialog */}
      <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Disable Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              This will make your account less secure. Enter your current 2FA code to confirm.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Alert className="bg-red-50 border-red-200">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-700">
                Disabling 2FA will remove the extra layer of protection from your account. 
                We strongly recommend keeping it enabled for accounts with financial access.
              </AlertDescription>
            </Alert>
            
            <div className="space-y-2">
              <Label htmlFor="disable-code">Current 2FA Code</Label>
              <Input
                id="disable-code"
                type="text"
                inputMode="numeric"
                placeholder="Enter 6-digit code"
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                className="text-center text-lg tracking-widest"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisableDialog(false)}>
              Keep 2FA Enabled
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDisable2FA}
              disabled={actionLoading || disableCode.length < 6}
            >
              {actionLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Disabling...</>
              ) : (
                'Disable 2FA'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default Security;
